import { Icon } from "@/assets/icons";
import { useAuth } from "@/contexts/AuthContext";
import MyNotification from "@/pages/Notifications/MyNotification/MyNotification";
import { useLoadingStore } from "@/store/loadingStore";
import { ECOMPANY_BUSINESS, RoleEnum } from "@/types";
import { isDesktop } from "@/utils/utilsDevice";
import React, { useEffect, useState } from "react";
import { HiUsers } from "react-icons/hi";
import { IoMdNotifications } from "react-icons/io";
import { LuPanelLeftOpen } from "react-icons/lu";
import {
  MdEmail,
  MdHistory,
  MdLocalActivity,
  MdLogout,
  MdOutlineWork,
  MdPermContactCalendar,
} from "react-icons/md";
import { Link, Outlet, useLocation } from "react-router-dom";
import AvatarDefault from "../assets/AvatarDefault.svg";
import logo from "../assets/logo.png";
import { ROUTES } from "../constants/routes";
import { Accordion, CollapseItem } from "./Accordion";
import { Button } from "./Button";
import { Loading } from "./Loading";
import Popover from "./Popover";
import { cn } from "@/utils";

interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string;
  children?: {
    key: string;
    label: string | React.ReactNode;
  }[];
  otherPath?: string[];
  hidden?: boolean;
}

const SIDEBAR_COLLAPSED = 'sidebar-collapsed';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const sidebarCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED);
  const sidebarVisible = isDesktop()
    ? sidebarCollapsed
      ? sidebarCollapsed == "false"
      : true
    : false;
  const [sidebarOpen, setSidebarOpen] = useState(sidebarVisible);
  const { isLoading } = useLoadingStore();
  const { logout, role, user } = useAuth();

  const isStoreInType4Or5Company = user?.company.business
    ? !![ECOMPANY_BUSINESS.CS_RETAIL, ECOMPANY_BUSINESS.TS_RETAIL].includes(
        user?.company.business,
      )
    : false;

  const showNotification = role !== RoleEnum.OTHER;

  const ELEARNING_ITEM: MenuItem = {
    key: ROUTES.E_LEARNING,
    icon: <MdOutlineWork className="w-6 h-6" />,
    label: "Eラーニング",
  };
  const COMPANY_ITEM: MenuItem = {
    key: ROUTES.COMPANY,
    icon: <HiUsers className="w-6 h-6" />,
    label: "ユーザー管理 ",
    otherPath: ["/store/", "/users", "/user-information"],
  };
  const STORE_ITEM: MenuItem = {
    key: `${ROUTES.STORE}/${user?.store_id}`,
    icon: <HiUsers className="w-6 h-6" />,
    label: "ユーザー管理 ",
    otherPath: ["/users", "/user-information"],
  };
  const NOTIFICATION_ITEM: MenuItem = {
    key: ROUTES.NOTIFICATION_MANAGEMENT,
    icon: <IoMdNotifications className="w-6 h-6" />,
    label: "通知管理",
  };
  const CHAT_ITEM: MenuItem = {
    key: ROUTES.CHAT,
    icon: <MdLocalActivity className="w-6 h-6" />,
    label: "事前相談",
  };
  const MY_PROFILE_ITEM: MenuItem = {
    key: ROUTES.ME,
    icon: <MdPermContactCalendar className="w-6 h-6" />,
    label: "情報",
  };
  const INSPECTION_ITEM: MenuItem = {
    key: ROUTES.INSPECTION,
    icon: <MdEmail className="w-6 h-6" />,
    label: "随時点検",
    hidden: !isStoreInType4Or5Company,
  };
  const STORE_INSPECTION_RESULTS_ITEM: MenuItem = {
    key: ROUTES.STORE_INSPECTION_RESULTS,
    icon: <MdHistory className="w-6 h-6" />,
    label: "店舗巡回結果",
    hidden: role !== RoleEnum.STORE_MANAGER,
    otherPath: ["/store-inspection-results/"],
  };
  const MONTHLY_CHECK_ITEM: MenuItem = {
    key: ROUTES.MONTHLY_CHECK,
    icon: <MdEmail className="w-6 h-6" />,
    label: "随時点検",
  };

  const MenuItemForRole = {
    [RoleEnum.SUPER_ADMIN]: [],
    [RoleEnum.COMPANY_ADMIN]: [
      ELEARNING_ITEM,
      COMPANY_ITEM,
      CHAT_ITEM,
      MONTHLY_CHECK_ITEM,
      NOTIFICATION_ITEM,
    ],
    [RoleEnum.HEAD_OFFICE_USER]: [
      ELEARNING_ITEM,
      COMPANY_ITEM,
      CHAT_ITEM,
      NOTIFICATION_ITEM,
    ],
    [RoleEnum.STORE_MANAGER]: [
      ELEARNING_ITEM,
      STORE_ITEM,
      CHAT_ITEM,
      STORE_INSPECTION_RESULTS_ITEM,
      INSPECTION_ITEM,
      NOTIFICATION_ITEM,
    ],
    [RoleEnum.USER]: [ELEARNING_ITEM, CHAT_ITEM, NOTIFICATION_ITEM],
    [RoleEnum.OTHER]: [CHAT_ITEM, MY_PROFILE_ITEM],
  };

  const menuItems: MenuItem[] = MenuItemForRole[role || RoleEnum.OTHER];

  useEffect(() => {
    if (!isDesktop()) return;
    localStorage.setItem(SIDEBAR_COLLAPSED, sidebarOpen ? "false" : "true");
  }, [sidebarOpen, isDesktop]);

  const handleLogout = () => {
    // Logout chỉ là clear local data, không cần async
    logout();
  };

  const isPathActiveNoChild = (item: MenuItem) => {
    return (
      location.pathname.includes(item.key) ||
      item.otherPath?.some((path) => location.pathname.includes(path))
    );
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div
        className={cn(`
        fixed inset-y-0 left-0 z-50 w-64 transform transition-transform
        lg:translate-x-0 lg:inset-0 lg:transition-[width] lg:overflow-hidden
        bg-black duration-300 ease-in-out
        ${
          sidebarOpen
            ? "translate-x-0 lg:w-[17.5rem]"
            : "-translate-x-full lg:w-[4.5rem] xl:w-[6.25rem]"
        }
      `)}
      >
        <div
          className={`flex items-center justify-between p-6 ${
            sidebarOpen
              ? ""
              : "pt-[3.25rem] !px-1 !justify-center lg:!pb-14 xl:!pb-6 xl:!px-6"
          }`}
        >
          <img
            src={logo}
            alt="logo"
            className={`transition-all duration-300 cursor-pointer ${
              sidebarOpen ? "h-[3.75rem]" : "h-[1.625rem]"
            }`}
            onClick={() => {
              !sidebarOpen && setSidebarOpen(true);
            }}
          />
          {sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="hover:opacity-50"
            >
              <Icon name="left-panel-close" />
            </button>
          )}
        </div>

        <nav className="text-sm">
          {menuItems.map((item) => {
            // Skip hidden items
            if (item.hidden) return null;

            // Nếu item có children, render accordion
            if (item.children && sidebarOpen) {
              return (
                <div
                  key={item.key}
                  className={cn(`
                    w-full items-center flex text-left gap-4
                    ${
                      location.pathname.includes(item.key)
                        ? "text-cyan"
                        : "text-auro_metal_saurus"
                    }
                  `)}
                >
                  <Accordion
                    items={[item as CollapseItem]}
                    triggerClassName="hover:bg-catalina_blue hover:bg-opacity-20 hover:text-cyan px-6"
                    childItemClassName="hover:bg-catalina_blue hover:bg-opacity-20 hover:text-cyan pl-[3.75rem]"
                    getChildItemClassName={(child) =>
                      isPathActiveNoChild(child as any)
                        ? "!text-cyan"
                        : "!text-auro_metal_saurus"
                    }
                    customItemRender={(i) => <Link to={i.key}>{i.label}</Link>}
                  />
                </div>
              );
            }

            // Item không có children - giữ nguyên hover effect
            if (item.children && !sidebarOpen) {
              return (
                <Popover
                  key={item.key}
                  trigger={
                    <div
                      key={item.key}
                      className={cn(`
                        w-full flex py-3 text-left transition-colors duration-200 gap-4 hover:bg-catalina_blue/20 hover:text-cyan
                        ${
                          sidebarOpen
                            ? "px-6 items-center"
                            : "px-4 justify-center"
                        }
                        ${
                          location.pathname.includes(item.key)
                            ? "text-cyan"
                            : "text-auro_metal_saurus"
                        }`)}
                    >
                      {item.icon}
                    </div>
                  }
                >
                  <div className="ml-2 -translate-y-[30%] rounded-lg min-w-40 overflow-hidden bg-black">
                    {item.children?.map((child) => (
                      <div
                        key={child.key}
                        className={cn(
                          "text-auro_metal_saurus text-sm leanding-[1.375rem] font-normal p-4 hover:bg-catalina_blue/20 hover:text-cyan cursor-pointer",
                          isPathActiveNoChild(child as any)
                            ? "!text-cyan"
                            : "!text-auro_metal_saurus",
                        )}
                      >
                        <Link to={child.key}>{child.label}</Link>
                      </div>
                    ))}
                  </div>
                </Popover>
              );
            }

            return (
              <Link
                to={item.key}
                key={item.key}
                className={cn(`
                w-full flex py-3 text-left transition-colors duration-200 gap-4 hover:bg-catalina_blue/20 hover:text-cyan
                ${sidebarOpen ? "px-6 items-center" : "px-4 justify-center"}
                ${
                  isPathActiveNoChild(item)
                    ? "text-cyan"
                    : "text-auro_metal_saurus"
                }`)}
                title={!sidebarOpen ? item.label : undefined}
                onClick={(e) => {
                  if (isDesktop()) return;
                  e.stopPropagation();
                  setSidebarOpen(false);
                }}
              >
                {item.icon}
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {!sidebarOpen && (
          <div className="fixed lg:top-4 xl:top-auto xl:bottom-10 flex justify-center w-full">
            <button
              onClick={() => setSidebarOpen(true)}
              className="hover:opacity-50"
            >
              <Icon name="left-panel-close" className="rotate-180" />
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div
        className={`flex-1 flex flex-col min-w-0 ${
          sidebarOpen ? "lg:ml-[17.5rem]" : "lg:ml-[4.5rem] xl:ml-[6.25rem]"
        }`}
      >
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-grey-600/15 h-[3.25rem] px-4 py-2 flex items-center justify-between gap-6 fixed top-0 left-0 right-0 z-20">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden"
          >
            <LuPanelLeftOpen className="size-5 ml-2" />
          </button>
          <div className="relative ml-auto">
            {showNotification && <MyNotification />}
          </div>

          <div className="flex items-center gap-3">
            {/* avatar  */}
            <div className="size-9 rounded-full bg-catalina_blue/20 flex items-center justify-center cursor-pointer">
              <Link to={ROUTES.ME}>
                <img
                  src={user?.image || AvatarDefault}
                  alt="avatar"
                  className="size-9 rounded-full object-cover"
                />
              </Link>
            </div>
            {/* logout  */}
            <Button
              onClick={handleLogout}
              buttonType="text"
              className="flex items-center gap-2 px-2 py-1 bg-white hover:bg-gray-50 transition-colors"
              title="ログアウト"
            >
              <MdLogout className="w-5 h-5 text-error_main" />
              <span className="font-medium text-error_main text-sm">
                ログアウト
              </span>
            </Button>
          </div>
        </header>
        <div className="h-[3.25rem]" />

        {/* Page content */}
        <main className="flex-1 px-4 2xl:px-8 min-w-0">
          <Outlet />
        </main>
      </div>

      {isLoading && <Loading />}
    </div>
  );
};

export default AppLayout;
